import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import {
  PartnerAssetDto,
  PartnerDebtDto,
  PatrimonialMutationRowDto,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import {
  ASSET_FILTERS,
  AssetFilter,
  ELIGIBILITY_TAGS,
  Tone,
  VALUE_SOURCE_LABELS,
  assetGroupLabel,
  countAssetsByFilter,
  describeAssetChange,
  describeAssetSituation,
  describeSecuringDebts,
  formatAssetName,
  fineThousandsDigits,
  formatRatio,
  formatThousandsFine,
  signTone,
  valueForYear,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';

/** Anos mostrados de saída na tabela de bens; "Ver os 4 anos" abre o resto. */
const DEFAULT_VISIBLE_YEARS = 2;
/** Grupos abertos de saída: os 2 maiores pelo saldo do último ano. */
const DEFAULT_OPEN_GROUPS = 2;

function isAwaitingPolicyNote(note: string): boolean {
  return note
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .startsWith('aguardando politica');
}

/**
 * "Bens e direitos para garantia": saldos dos anos mais recentes (2 de saída, todos com "Ver os 4 anos"), valor de
 * referência, haircut, ônus e comprometido, livre para garantia e situação, com filtro por situação. Sem política de
 * garantias, as três colunas que dependem dela somem (o aviso fica uma vez só, no topo da aba).
 */
@Component({
  selector: 'app-pit-assets-table',
  standalone: true,
  templateUrl: './assets-table.component.html',
  styleUrl: './assets-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetsTableComponent {
  readonly assets = input.required<PartnerAssetDto[]>();
  readonly years = input.required<number[]>();
  readonly policyDefined = input(true);
  /** Linha "Bens e direitos" da mutação patrimonial, para o rodapé (totais por ano). */
  readonly totalRow = input<PatrimonialMutationRowDto | null>(null);
  /** Capacidade livre total (com e sem restrição), do indicador do sócio. */
  readonly freeTotal = input<number | null>(null);
  readonly canEdit = input(false);
  /** Dívidas do sócio, para dizer no bem qual dívida ele garante (aparece mesmo sem política). */
  readonly debts = input<PartnerDebtDto[]>([]);
  /** Linhas da mutação patrimonial: a de cada grupo dá o subtotal por ano do agrupamento. */
  readonly groupRows = input<PatrimonialMutationRowDto[]>([]);

  readonly edit = output<PartnerAssetDto>();

  /** Grupos abertos pela pessoa; `null` = os 2 maiores (padrão). Volta ao padrão a cada sócio/ano novo. */
  protected readonly openGroups = linkedSignal<PartnerAssetDto[], Set<string> | null>({
    source: this.assets,
    computation: () => null,
  });

  protected readonly filter = signal<AssetFilter>('all');
  protected readonly showAllYears = signal(false);
  protected readonly filters = ASSET_FILTERS;
  protected readonly tags = ELIGIBILITY_TAGS;
  protected readonly sources = VALUE_SOURCE_LABELS;
  protected readonly ratio = formatRatio;

  /**
   * Casas decimais da tabela inteira (bens, subtotais, rodapé, colunas da garantia), de todos os bens e não só dos
   * filtrados: trocar o filtro não muda a grafia dos números.
   */
  protected readonly digits = computed(() => {
    const years = this.visibleYears();
    const series = [
      ...this.assets().map(asset => asset.values),
      ...this.groupRows().map(row => row.values),
      this.totalRow()?.values ?? [],
    ];
    return fineThousandsDigits([
      ...series.flatMap(values => years.map(year => valueForYear(values, year))),
      ...this.assets().flatMap(asset => [
        asset.referenceValue,
        asset.encumbrance + asset.committed,
        asset.freeValue,
      ]),
      this.freeTotal(),
    ]);
  });

  protected thousands(value: number | null | undefined): string {
    return formatThousandsFine(value, this.digits());
  }

  protected readonly lastYear = computed(() => {
    const years = this.years();
    return years.length ? years[years.length - 1] : null;
  });

  protected readonly hasHiddenYears = computed(() => this.years().length > DEFAULT_VISIBLE_YEARS);

  /** Anos em coluna: os 2 mais recentes de saída; todos com "Ver os 4 anos". */
  protected readonly visibleYears = computed(() =>
    this.showAllYears() ? this.years() : this.years().slice(-DEFAULT_VISIBLE_YEARS)
  );

  protected readonly counts = computed(() => countAssetsByFilter(this.assets()));

  /** Colunas da tabela, para o `colspan` da linha "nenhum bem". */
  protected readonly columnCount = computed(
    () =>
      1 +
      this.visibleYears().length +
      2 +
      (this.policyDefined() ? 3 : 0) +
      1 +
      (this.canEdit() ? 1 : 0)
  );

  protected readonly rows = computed(() => {
    const filter = this.filter();
    const years = this.years();
    const visible = this.visibleYears();
    const debts = this.debts();
    const lastYear = this.lastYear();
    return this.assets()
      .filter(asset => filter === 'all' || asset.eligibility === filter)
      .map(asset => ({
        asset,
        name: formatAssetName(asset.shortName),
        values: visible.map(year => this.thousands(valueForYear(asset.values, year))),
        change: describeAssetChange(asset, years),
        caption: `${asset.group} · ${asset.groupLabel}`,
        // "Aguardando política de garantias" já está no aviso do topo da aba: não repete embaixo de cada bem.
        notes: [asset.eligibilityReason, ...asset.restrictions].filter(
          (note): note is string => !!note && !isAwaitingPolicyNote(note)
        ),
        // Dívida que o bem garante (N3): o imóvel não pode parecer livre quando está alienado.
        securedDebts: describeSecuringDebts(asset, debts, lastYear),
        counts: asset.eligibility !== 'inelegivel' && asset.eligibility !== 'vendido',
        encumbrance: asset.encumbrance + asset.committed,
        situation: describeAssetSituation(asset),
      }));
  });

  /**
   * Bens agrupados pelo grupo da ficha (Imóveis, Veículos, Participações...), na ordem da declaração. O subtotal por
   * ano e a variação vêm da linha do grupo na mutação patrimonial (backend), não de soma na tela. Abertos de saída: os
   * 2 maiores grupos pelo saldo do último ano; a escolha da pessoa vale até trocar de sócio.
   */
  protected readonly groups = computed(() => {
    const rows = this.rows();
    const visible = this.visibleYears();
    const lastYear = this.lastYear();
    const mutation = new Map(this.groupRows().map(row => [row.group, row]));
    const byGroup = new Map<string, typeof rows>();
    for (const row of rows) {
      const list = byGroup.get(row.asset.group) ?? [];
      list.push(row);
      byGroup.set(row.asset.group, list);
    }
    const groups = [...byGroup.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([code, items]) => {
        const total = mutation.get(code) ?? null;
        const pct = total?.lastYearChangePct ?? null;
        return {
          code,
          label: assetGroupLabel(code, items[0]?.asset.groupLabel),
          count: items.length,
          rows: items,
          subtotals: visible.map(year =>
            total ? this.thousands(valueForYear(total.values, year)) : ''
          ),
          change: { text: pct == null ? '' : formatRatio(pct, 0, true), tone: signTone(pct) },
          lastValue: total && lastYear != null ? (valueForYear(total.values, lastYear) ?? 0) : 0,
        };
      });
    const chosen = this.openGroups();
    const defaults = new Set(
      [...groups]
        .sort((a, b) => b.lastValue - a.lastValue)
        .slice(0, DEFAULT_OPEN_GROUPS)
        .map(group => group.code)
    );
    return groups.map(group => ({
      ...group,
      open: chosen ? chosen.has(group.code) : defaults.has(group.code),
    }));
  });

  protected toggleGroup(code: string): void {
    const current = new Set(
      this.openGroups() ??
        this.groups()
          .filter(group => group.open)
          .map(group => group.code)
    );
    if (current.has(code)) current.delete(code);
    else current.add(code);
    this.openGroups.set(current);
  }

  /** Totais por ano do rodapé: sempre de todos os bens (o rótulo muda quando há filtro). */
  protected readonly footerValues = computed(() => {
    const total = this.totalRow();
    return this.visibleYears().map(year =>
      total ? this.thousands(valueForYear(total.values, year)) : ''
    );
  });

  protected readonly totalChange = computed<{ text: string; tone: Tone }>(() => {
    const pct = this.totalRow()?.lastYearChangePct ?? null;
    return { text: pct == null ? '' : formatRatio(pct, 0, true), tone: signTone(pct) };
  });

  protected toggleYears(): void {
    this.showAllYears.update(value => !value);
  }
}
